import React, { useState, useEffect } from 'react';
import './BoostModal.css';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe (requires publishable key)
// We'll fetch this from the backend or env later
// const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');

interface UserListing {
    id: string;
    title: string;
    pictures?: string[];
    monthly_rent?: number;
    university_name?: string;
}

interface BoostModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialListingId?: string;
    listings?: UserListing[]; // If not provided, will be fetched
}

const BoostModal: React.FC<BoostModalProps> = ({ isOpen, onClose, initialListingId, listings: propListings }) => {
    const { token } = useAuth();
    const [listings, setListings] = useState<UserListing[]>(propListings || []);
    const [selectedListingId, setSelectedListingId] = useState<string>(initialListingId || '');
    const [loading, setLoading] = useState(false);
    const [fetchingListings, setFetchingListings] = useState(!propListings);
    const [error, setError] = useState<string | null>(null);

    // Sync listings state with propListings when they are provided from parent
    useEffect(() => {
        if (propListings) {
            setListings(propListings);
            setFetchingListings(false);
            if (propListings.length > 0 && !selectedListingId) {
                setSelectedListingId(propListings[propListings.length - 1].id);
            }
        }
    }, [propListings]);

    // Prefetch listings eagerly on mount if no props provided
    useEffect(() => {
        if (!propListings && token && listings.length === 0) {
            fetchListings();
        }
    }, [token, propListings]);

    // When modal opens, just ensure selection is correct
    useEffect(() => {
        if (isOpen) {
            setSelectedListingId(initialListingId || selectedListingId || (listings.length > 0 ? listings[listings.length - 1].id : ''));
            
            // If listings haven't loaded yet (edge case), fetch them
            if (!propListings && token && listings.length === 0 && !fetchingListings) {
                fetchListings();
            }
        }
    }, [isOpen, initialListingId]);

    const fetchListings = async () => {
        setFetchingListings(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/users/listings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                const fetched = data.data || [];
                setListings(fetched);
                if (fetched.length > 0 && !initialListingId) {
                    setSelectedListingId(fetched[fetched.length - 1].id);
                }
            }
        } catch (err) {
            console.error('Error fetching listings for boost:', err);
        } finally {
            setFetchingListings(false);
        }
    };

    const handleBoost = async (type: 'day' | 'week') => {
        if (!selectedListingId) {
            setError('Please select a listing to boost.');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/subleases/${selectedListingId}/boost`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ boost_type: type })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.checkout_url) {
                    window.location.href = data.checkout_url;
                } else {
                    setError('Unable to initiate checkout.');
                }
            } else {
                const errData = await response.json();
                setError(errData.detail || 'Failed to initialize boost. Please try again later.');
            }
        } catch (err) {
            setError('A network error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        } else if (shouldRender) {
            setIsClosing(true);
            const timer = setTimeout(() => {
                setShouldRender(false);
                setIsClosing(false);
            }, 300); // Match CSS animation duration
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    if (!shouldRender) return null;

    return (
        <div 
            className={`boost-modal-overlay ${isClosing ? 'is-closing' : ''}`} 
            onClick={onClose}
        >
            <div 
                className={`boost-modal-content ${isClosing ? 'is-closing' : ''}`} 
                onClick={(e) => e.stopPropagation()}
                style={{ fontFamily: "'DM Mono', monospace" }}
            >
                <button className="boost-modal-close" onClick={onClose} aria-label="Close modal">×</button>
                
                <div className="boost-modal-header">
                    <h2>Boost Your Listing</h2>
                    <p><span className="boost-highlight">10x</span> more views by featuring your listing at the top of search results.</p>
                </div>

                <div className="boost-modal-body">
                    {fetchingListings ? (
                        <div className="boost-loading">Loading your listings...</div>
                    ) : (
                        <>
                            {listings.length === 0 ? (
                                <div className="boost-no-listings">
                                    You don't have any listings to boost yet.
                                </div>
                            ) : (
                                <>
                                    {/* Smart Selection & Preview Logic */}
                                    <div className="boost-preview-section">
                                        <div className="boost-preview-card">
                                            {(() => {
                                                const selectedListing = listings.find(l => l.id === selectedListingId);
                                                if (!selectedListing) return null;
                                                
                                                const firstImage = selectedListing.pictures?.[0];
                                                const displayImage = firstImage 
                                                    ? (firstImage.startsWith('http') ? firstImage : `${API_BASE_URL}${firstImage}`)
                                                    : null;

                                                return (
                                                    <>
                                                        <div className="preview-image">
                                                            {displayImage ? (
                                                                <img src={displayImage} alt={selectedListing.title} />
                                                            ) : (
                                                                <div className="preview-image-placeholder">No Image</div>
                                                            )}
                                                        </div>
                                                        <div className="preview-info">
                                                            <h4>{selectedListing.title}</h4>
                                                            <div className="preview-meta">
                                                                {selectedListing.monthly_rent && <span>${selectedListing.monthly_rent}/mo</span>}
                                                                {selectedListing.university_name && <span> • {selectedListing.university_name}</span>}
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>

                                        {/* Only show selector if opened from nav bar AND user has multiple listings */}
                                        {listings.length > 1 && !initialListingId && (
                                            <div className="boost-select-wrapper">
                                                <select 
                                                    value={selectedListingId} 
                                                    onChange={(e) => setSelectedListingId(e.target.value)}
                                                    className="boost-listing-select"
                                                >
                                                    {listings.map(l => (
                                                        <option key={l.id} value={l.id}>{l.title}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                    </div>

                                    <div className="boost-options-container">
                                        <div className="boost-option-card">
                                            <div className="boost-option-header">
                                                <h3>1 Day Boost</h3>
                                                <span className="boost-price">$1.99</span>
                                            </div>

                                            <button 
                                                className="boost-btn boost-btn-secondary"
                                                onClick={() => handleBoost('day')}
                                                disabled={loading}
                                            >
                                                Day Boost
                                            </button>
                                        </div>

                                        <div className="boost-option-card boost-best-value">
                                            <div className="best-value-badge">BEST VALUE</div>
                                            <div className="boost-option-header">
                                                <h3>1 Week Boost</h3>
                                                <span className="boost-price">$4.99</span>
                                            </div>

                                            <button 
                                                className="boost-btn boost-btn-primary"
                                                onClick={() => handleBoost('week')}
                                                disabled={loading}
                                            >
                                                Week Boost
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {error && <div className="boost-error-message">{error}</div>}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BoostModal;
